/**
 * The rules engine. Pure: it reads rows and settings through a DataContext and returns numbers.
 * Ported from the prototype's `Engine`; behaviour must stay identical (see test/engine.test.ts).
 *
 * The recommendation order in decide() is deliberate; don't reorder it without asking.
 */
import type { AiEstimate, CustomsMap, DataContext, Decision, MoveCost, Row, ScenarioTotal } from './types';
import { AI_MODELS, DEFAULTS, DONE, catLabel } from './constants';
import { J, fmtCur, money, num, sum, today } from './util';

export interface Lookups {
  dests(): Row[];
  scenariosFor(d: Row | undefined): Row[];
  ruleFor(d: Row | undefined): Row & { excluded: string[] };
  hasAppraisal(it: Row): boolean;
  weightDefault(cat: string): Row | undefined;
}

export function createLookups(ctx: DataContext): Lookups {
  return {
    dests: () => ctx.rows('destinations'),
    scenariosFor: d => ctx.rows('scenarios').filter(s => s.destination_id === d?.id),
    ruleFor(d) {
      const r = ctx.rows('customs_rules').find(x => x.country_code === d?.country_code) || {};
      return { min_prior_residence_months: 12, min_ownership_months: 6, newer_item_allowance: 0, allowance_currency: 'USD', arrival_before_months: 0, arrival_after_months: 12, import_vat_rate: 0, ...r, excluded: J<string[]>(r.excluded_categories, []) };
    },
    hasAppraisal: it => ctx.rows('appraisals').some(a => a.item_id === it.id && a.value != null),
    weightDefault: cat => ctx.rows('weight_defaults').find(w => w.category === cat),
  };
}

export function createEngine(ctx: DataContext) {
  const L = createLookups(ctx);
  const S = (k: string) => { const v = ctx.setting(k); return v === undefined ? DEFAULTS[k] : v; };
  let memo: Record<string, any> = {};

  const E = {
    reset() { memo = {}; },
    mid(lo: any, hi: any): number | null { if (lo == null && hi == null) return null; if (lo == null) return +hi; if (hi == null) return +lo; return (+lo + +hi) / 2; },
    monthsBetween(a?: string | null, b?: string | null): number | null {
      if (!a || !b) return null;
      const A = new Date(a + 'T12:00:00'), B = new Date(b + 'T12:00:00');
      let m = (B.getFullYear() - A.getFullYear()) * 12 + (B.getMonth() - A.getMonth());
      if (B.getDate() < A.getDate()) m--;
      return m;
    },
    /** Total cubic feet for the record as it ships. */
    volume(it: Row): number {
      if (it.volume_cuft > 0) return +it.volume_cuft;
      if (it.length_in && it.width_in && it.height_in) return it.length_in * it.width_in * it.height_in / 1728;
      const wd = L.weightDefault(it.category);
      if (wd && wd.unit === 'lb_per_cuft' && it.weight_lb) return it.weight_lb / wd.value;
      return 1;
    },
    /** Total pounds for the record; falls back to the editable weight defaults. */
    weight(it: Row): number {
      if (it.weight_lb > 0) return +it.weight_lb;
      const wd = L.weightDefault(it.category), v = E.volume(it);
      if (!wd) return v * 8;
      switch (wd.unit) {
        case 'lb_each': return wd.value * (it.qty || 1);
        case 'lb_per_cuft': return wd.value * v;
        case 'lb_per_sqft': return wd.value * ((it.length_in && it.width_in) ? it.length_in * it.width_in / 144 : v / 3);
        case 'lb_per_box': return wd.value * v / (wd.box_cuft || 1.5);
      }
      return v * 8;
    },
    isBulky(it: Row) { return ['furniture', 'upholstered', 'outdoor', 'sports', 'appliance'].includes(it.category) && E.volume(it) > 4; },
    /** Flat-rate container cost per packed ft³, spread over the Keep list and floored at half a container. */
    containerRate(sc: Row): number {
      const key = 'cr_' + sc.id; if (memo[key] != null) return memo[key];
      const cap = sc.capacity_cuft || 1000, allow = 1 + num(S('packing_allowance'));
      const packed = sum(ctx.rows('items').filter(i => i.action === 'keep' && !DONE.includes(i.status)), i => E.volume(i) * allow);
      const n = Math.max(1, Math.ceil(packed / cap));
      return (memo[key] = n * sc.rate / Math.max(packed, cap * 0.5));
    },
    /** Per-item marginal cost of moving under one scenario. Shipment minimums and fixed fees are in scenarioTotal. */
    moveCost(it: Row, sc: Row | undefined): MoveCost {
      if (!sc) return { total: Infinity, feasible: false, why: 'No scenario' };
      const vol = E.volume(it), packed = vol * (1 + num(S('packing_allowance'))), w = E.weight(it);
      let freight = 0, feasible = true, why = '';
      switch (sc.rate_unit) {
        case 'cuft': freight = packed * sc.rate; break;
        case 'container': freight = packed * E.containerRate(sc); break;
        case 'lb': {
          const div = num(S('air_dim_divisor')) || 139;
          const dim = (it.length_in && it.width_in && it.height_in) ? it.length_in * it.width_in * it.height_in / div : packed * 1728 / div;
          if (sc.max_piece_lb && w > sc.max_piece_lb) { feasible = false; why = `Over the ${sc.max_piece_lb} lb air-freight piece limit`; }
          freight = Math.max(w, dim) * sc.rate; break;
        }
        case 'bag': {
          if (vol > 3) { feasible = false; why = 'Too big to carry as luggage'; }
          freight = Math.ceil(w / (sc.max_piece_lb || 50)) * sc.rate; break;
        }
      }
      const materials = (E.isBulky(it) ? 0 : packed / 3) * num(S('materials_per_box'));
      const value = it.declared_value || E.mid(it.replacement_low, it.replacement_high) || 0;
      const crate = it.fragile && value > num(S('crate_over')) ? num(S('crate_cost')) : 0;
      const insurance = value * num(S('insurance_pct'));
      return { total: feasible ? freight + materials + crate + insurance : Infinity, freight, materials, crate, insurance, packed, weight: w, feasible, why };
    },
    customsValue(it: Row): number { return it.declared_value || E.mid(it.resale_low, it.resale_high) || it.purchase_price || 0; },
    /** Per-destination eligibility for every item. The newer-item allowance is consumed by kept items in purchase-date order. */
    customsMap(d: Row): CustomsMap {
      const key = 'cm_' + d.id; if (memo[key]) return memo[key];
      const rule = L.ruleFor(d), map: any = {}, newer: Row[] = [];
      const at = d.move_date || d.residence_date || today();
      const fx = rule.allowance_currency === 'ISK' ? (num(S('usd_to_isk')) || 140) : 1;
      for (const it of ctx.rows('items')) {
        const owned = E.monthsBetween(it.acquired_date, at);
        const cat = it.excluded_category || it.category;
        let status = 'ok', note = '';
        if (rule.excluded.includes(cat)) { status = 'excluded'; note = `${catLabel(cat)} isn't covered by household-goods relief`; }
        else if (it.business_use && rule.business_gear_excluded) { status = 'business'; note = "Business equipment isn't covered"; }
        else if (owned == null) { status = 'nodate'; note = 'Add a purchase date or receipt to confirm'; }
        else if (owned < rule.min_ownership_months) {
          if (rule.newer_item_allowance > 0) { status = 'allowance'; newer.push(it); }
          else { status = 'new'; note = `Owned ${Math.max(0, owned)} of ${rule.min_ownership_months} months at the move`; }
        }
        map[it.id] = { status, note, owned };
      }
      newer.sort((a, b) => (a.acquired_date || '').localeCompare(b.acquired_date || ''));
      let used = 0;
      for (const it of newer) {
        const val = (it.purchase_price || E.mid(it.resale_low, it.resale_high) || 0) * fx;
        const c = map[it.id];
        if (it.action === 'keep') {
          if (used + val <= rule.newer_item_allowance) { used += val; c.note = `Uses ${fmtCur(val, rule.allowance_currency)} of the allowance`; }
          else { c.status = 'over'; c.note = 'Over the new-goods allowance; import VAT applies'; }
        } else c.note = used + val <= rule.newer_item_allowance ? `Would use ${fmtCur(val, rule.allowance_currency)} of the allowance` : 'Would exceed the new-goods allowance';
        c.value_local = val;
      }
      map._used = used; map._total = rule.newer_item_allowance; map._cur = rule.allowance_currency;
      return (memo[key] = map);
    },
    dutyRisk(it: Row, d: Row): number {
      const c = E.customsMap(d)[it.id]; if (!c) return 0;
      const dutiable = ['new', 'business', 'excluded', 'over'].includes(c.status) || (c.status === 'allowance' && it.action !== 'keep' && /exceed/.test(c.note));
      return dutiable ? E.customsValue(it) * L.ruleFor(d).import_vat_rate : 0;
    },
    /** The verdict. Rule order matters. */
    decide(it: Row, d: Row, sc: Row | undefined): Decision {
      const sellMin = num(S('sell_min'));
      const mc = E.moveCost(it, sc);
      const replace = it.needed === 0 ? 0 : E.mid(it.replacement_low, it.replacement_high);
      const resale = E.mid(it.resale_low, it.resale_high) || 0;
      const duty = E.dutyRisk(it, d);
      const hassle = num(S('hassle_factor'));
      const keepScore = !mc.feasible ? -Infinity : replace == null ? null : replace - resale - mc.total - duty - hassle;
      const c = E.customsMap(d)[it.id] || {};
      let rec: Decision['rec'], reason: string;
      if (it.sentimental) { rec = 'keep'; reason = 'Sentimental, so it comes whatever it costs.'; }
      else if (it.power_type === '120v_only') { rec = resale >= num(S('donate_120v_under')) ? 'sell' : 'donate'; reason = `Runs only on 120V; ${d.name} uses 230V.`; }
      else if (c.status === 'excluded') { rec = resale >= sellMin ? 'sell' : 'gift'; reason = `${c.note}, so it can't ship duty-free to ${d.name}.`; }
      else if (it.possibly_valuable && !L.hasAppraisal(it)) { rec = 'appraise'; reason = 'Possibly valuable. Get a human appraisal before deciding or pricing.'; }
      else if (!mc.feasible) { rec = resale >= sellMin ? 'sell' : 'donate'; reason = `${mc.why} in this mode; compare other modes.`; }
      else if (replace == null) { rec = 'review'; reason = 'Needs a replacement estimate before the math works.'; }
      else if ((keepScore as number) > 0) { rec = 'keep'; reason = `Replacing it costs ${money(keepScore)} more than shipping it.`; }
      else if (resale >= sellMin) { rec = 'sell'; reason = it.needed === 0 ? `Not needed in ${d.name}; sell it for about ${money(resale)}.` : `Shipping costs ${money(-(keepScore as number))} more than selling here and buying again.`; }
      else if (it.condition === 'poor') { rec = 'toss'; reason = 'Poor condition and little resale value.'; }
      else { rec = 'donate'; reason = 'Not worth shipping, and too little to sell.'; }
      return { rec, reason, move: mc.total, mc, replace, resale, duty, keepScore, customs: c };
    },
    best(it: Row, d: Row): { sc: Row; r: Decision } | null {
      let best: { sc: Row; r: Decision } | null = null;
      for (const sc of L.scenariosFor(d)) { const r = E.decide(it, d, sc); if (r.keepScore == null || !isFinite(r.keepScore)) continue; if (!best || r.keepScore > (best.r.keepScore as number)) best = { sc, r }; }
      return best;
    },
    /** Cost of shipping a whole list under one scenario, with minimums, fees and container counts. */
    scenarioTotal(sc: Row, items: Row[]): ScenarioTotal {
      let packed = 0, freight = 0, feasible = true;
      for (const it of items) { const m = E.moveCost(it, sc); if (!m.feasible) feasible = false; packed += m.packed || 0; freight += m.freight || 0; }
      let total: number, containers: number | null = null, fill: number | null = null;
      if (sc.rate_unit === 'cuft') total = Math.max(sc.minimum || 0, packed * sc.rate) + (sc.fixed_fees || 0);
      else if (sc.rate_unit === 'container') { const cap = sc.capacity_cuft || 1000; containers = Math.max(1, Math.ceil(packed / cap)); total = containers * sc.rate + (sc.fixed_fees || 0); fill = packed / (containers * cap); }
      else total = Math.max(sc.minimum || 0, freight) + (sc.fixed_fees || 0);
      return { total: feasible ? total : null, packed, feasible, containers, fill };
    },
    markdownPrice(si: Row, ev: Row | undefined, now = new Date()) {
      const days = ev?.starts_at ? Math.floor((now.getTime() - new Date(ev.starts_at + 'T00:00:00').getTime()) / 864e5) : 0;
      const steps = J<{ day: number; pct: number }[]>(ev?.markdowns, []); let pct = 0;
      for (const m of steps) if (days >= m.day) pct = Math.max(pct, m.pct);
      const next = steps.filter(m => m.day > days).sort((a, b) => a.day - b.day)[0];
      const nextDate = next && ev?.starts_at ? new Date(new Date(ev.starts_at + 'T12:00:00').getTime() + next.day * 864e5).toISOString().slice(0, 10) : null;
      return { price: Math.max(1, Math.round(si.list_price * (1 - pct / 100))), pct, next, nextPrice: next ? Math.max(1, Math.round(si.list_price * (1 - next.pct / 100))) : null, nextDate };
    },
    /** Estimated tokens, USD and Workers AI neurons for one enrichment call. */
    aiEstimate(photos: number, modelKey: string = S('ai_model')): AiEstimate {
      const m = AI_MODELS[modelKey] || ({} as any);
      const inTok = Math.round(num(S('ai_prompt_tokens')) + Math.max(1, photos) * num(S('ai_tokens_per_photo')));
      const outTok = Math.round(num(S('ai_output_tokens')));
      const usd = m.in == null || m.out == null ? null : (inTok * m.in + outTok * m.out) / 1e6;
      const neurons = usd == null ? null : usd / (num(S('neuron_usd_per_1000')) || 0.011) * 1000;
      return { inTok, outTok, usd, neurons };
    },
    /** Today's Workers AI usage (UTC day; the allowance resets at 00:00 UTC). */
    aiToday() {
      const day = new Date().toISOString().slice(0, 10);
      const jobs = ctx.rows('ai_jobs').filter(j => ['done', 'running'].includes(j.status) && (j.started_at || '').startsWith(day) && j.provider === 'workers-ai');
      const neurons = sum(jobs, j => j.actual_neurons ?? j.est_neurons), free = num(S('ai_free_neurons'));
      const overUsd = Math.max(0, neurons - free) * (num(S('neuron_usd_per_1000')) || 0.011) / 1000;
      return { neurons, free, jobs: jobs.length, overUsd, left: Math.max(0, free - neurons) };
    },
    /** Run now, or wait for the free allowance to reset? 'proceed' mode allows paid usage up to a daily cap. */
    aiGate(job: Row): { ok: boolean; why: string } {
      if (job.provider === 'anthropic') return { ok: true, why: 'Billed by Anthropic' };
      const t = E.aiToday(), est = job.est_neurons || 0;
      if (t.neurons + est <= t.free) return { ok: true, why: 'Within today’s free allowance' };
      const after = Math.max(0, t.neurons + est - t.free) * (num(S('neuron_usd_per_1000')) || 0.011) / 1000;
      if (S('ai_overage') === 'proceed' && after <= num(S('ai_daily_cap_usd'))) return { ok: true, why: `About $${after.toFixed(3)} over the free allowance today` };
      return { ok: false, why: 'today’s free AI allowance is used up; it runs after 00:00 UTC unless you approve the cost' };
    },
  };
  return E;
}
export type Engine = ReturnType<typeof createEngine>;
